import React from "react";
import { Link } from "react-router-dom";
import LegalPage from "../components/LegalPage";
import usePageTitle from "../hooks/usePageTitle";

/**
 * Политика конфиденциальности (обработки персональных данных) Рублокса.
 * Адаптирована с minecraftia-school.ru под бесплатную игровую платформу:
 * убраны разделы про оплату/эквайринг/подписку, упоминания школы/курсов
 * заменены на платформу/игры. Суть про детей и 152-ФЗ сохранена.
 *
 * Оператор тот же — ИП Иванкова В.С. (единая экосистема, единый аккаунт).
 */
export default function PrivacyPolicyPage() {
  usePageTitle("Политика конфиденциальности");
  return (
    <LegalPage
      title="Политика конфиденциальности"
      subtitle="Как мы обрабатываем персональные данные взрослых и детей. Соответствие 152-ФЗ. Редакция от 15 мая 2026 года."
      active="privacy"
    >
      <p>
        <em>Редакция от 15 мая 2026 года</em>
      </p>

      <p>
        Настоящая Политика обработки персональных данных (далее — Политика)
        разработана в соответствии с требованиями Федерального закона от
        27.07.2006 № 152-ФЗ «О персональных данных» и определяет порядок
        обработки персональных данных и меры по обеспечению их безопасности,
        предпринимаемые Оператором — Индивидуальным предпринимателем Иванковой
        В.С. (далее — Оператор).
      </p>

      <h2>1. Общие положения</h2>
      <p>
        1.1. Оператор ставит своей важнейшей целью и условием осуществления
        своей деятельности соблюдение прав и свобод человека и гражданина при
        обработке его персональных данных, в том числе защиты прав на
        неприкосновенность частной жизни, личную и семейную тайну.
      </p>
      <p>
        1.2. Настоящая Политика применяется ко всей информации, которую
        Оператор может получить о посетителях сайта{" "}
        <a href="https://rublox.pro">https://rublox.pro</a> и пользователях
        приложений игровой платформы «Рублокс» (далее — Платформа).
      </p>
      <p>
        1.3. Платформа «Рублокс» является частью экосистемы онлайн-школы
        программирования и математики «Майнкрафтия». Аккаунт пользователя —
        единый для Платформы и онлайн-школы.
      </p>

      <h2>2. Оператор персональных данных</h2>
      <p>
        <b>Индивидуальный предприниматель Иванкова Виктория Сергеевна</b>
      </p>
      <p>ОГРНИП: 322237500039230</p>
      <p>ИНН: 233507286445</p>
      <p>Адрес: Краснодарский край, г. Кореновск, ул. Мира 93</p>
      <p>
        E-mail для обращений по вопросам обработки персональных данных:{" "}
        <b>MaksimIvankov26@yandex.ru</b>
      </p>

      <h2>3. Основные понятия</h2>
      <p>
        3.1. <b>Персональные данные</b> — любая информация, относящаяся прямо
        или косвенно к определённому или определяемому физическому лицу
        (субъекту персональных данных).
      </p>
      <p>
        3.2. <b>Обработка персональных данных</b> — любое действие (операция) с
        персональными данными: сбор, запись, систематизация, накопление,
        хранение, уточнение, извлечение, использование, передача,
        обезличивание, блокирование, удаление, уничтожение.
      </p>
      <p>
        3.3. <b>Субъект персональных данных</b> — физическое лицо, к которому
        относятся персональные данные. В настоящей Политике субъектами являются
        Пользователи Платформы (совершеннолетние законные представители) и их
        несовершеннолетние дети (далее — Обучающиеся).
      </p>
      <p>
        3.4. <b>Оператор</b> — лицо, самостоятельно организующее и/или
        осуществляющее обработку персональных данных. В настоящей Политике
        Оператором является ИП Иванкова В.С.
      </p>

      <h2>4. Правовые основания обработки</h2>
      <p>4.1. Оператор обрабатывает персональные данные на следующих правовых основаниях:</p>
      <ul>
        <li>
          согласие субъекта персональных данных (ст. 6 ч. 1 п. 1 152-ФЗ),
          предоставленное путём акцепта{" "}
          <Link to="/terms">Пользовательского соглашения</Link> и настоящей
          Политики при регистрации на Платформе;
        </li>
        <li>
          исполнение соглашения, стороной которого является субъект
          персональных данных (ст. 6 ч. 1 п. 5 152-ФЗ);
        </li>
        <li>исполнение требований законодательства Российской Федерации.</li>
      </ul>

      <h2>5. Особенности обработки данных несовершеннолетних</h2>
      <p>
        5.1. Основной целевой аудиторией Платформы являются дети в возрасте от
        8 до 16 лет. Оператор осознаёт особую чувствительность обработки
        персональных данных несовершеннолетних и принимает все меры для их
        защиты.
      </p>
      <p>
        5.2. В соответствии со ст. 9 Федерального закона № 152-ФЗ, обработка
        персональных данных несовершеннолетнего осуществляется на основании
        согласия его <b>законного представителя</b> (родителя, усыновителя,
        опекуна или попечителя).
      </p>
      <p>
        5.3. Регистрация на Платформе осуществляется исключительно законным
        представителем несовершеннолетнего. Совершая регистрацию, законный
        представитель:
      </p>
      <ul>
        <li>
          подтверждает, что ему исполнилось 18 лет и он является родителем или
          иным законным представителем несовершеннолетнего Обучающегося;
        </li>
        <li>
          даёт согласие на обработку как своих персональных данных, так и
          персональных данных Обучающегося;
        </li>
        <li>
          несёт полную ответственность за решение о предоставлении Обучающемуся
          доступа к Платформе.
        </li>
      </ul>
      <p>
        5.4. Законный представитель вправе в любое время запросить удаление
        учётной записи Обучающегося и всех связанных с ней данных, направив
        заявление на адрес <b>MaksimIvankov26@yandex.ru</b>.
      </p>

      <h2>6. Категории и перечень обрабатываемых персональных данных</h2>
      <p>
        6.1. <b>Данные законного представителя (Пользователя):</b>
      </p>
      <ul>
        <li>имя, указанное при регистрации;</li>
        <li>адрес электронной почты;</li>
        <li>
          IP-адрес, тип устройства, версия приложения или браузера (технические
          данные для обеспечения безопасности и работоспособности сервиса).
        </li>
      </ul>
      <p>
        6.2. <b>Данные Обучающегося:</b>
      </p>
      <ul>
        <li>имя (псевдоним), указанное в профиле;</li>
        <li>аватар, если загружен;</li>
        <li>
          игровая активность: запуски игр, лайки, рейтинг, баланс внутриигровой
          валюты «алмазы»;
        </li>
        <li>
          контент, создаваемый в 3D-редакторе Рублокс (игры, модели, скрипты)
          — хранится для отображения на Платформе;
        </li>
        <li>
          иные данные, которые Обучающийся добровольно указывает в профиле.
        </li>
      </ul>
      <p>
        6.3. Оператор <b>не обрабатывает</b> специальные категории персональных
        данных (раса, национальность, политические взгляды, религиозные
        убеждения, данные о здоровье, биометрические данные).
      </p>
      <p>
        6.4. Платформа «Рублокс» предоставляется <b>бесплатно</b>. Оператор{" "}
        <b>не собирает и не обрабатывает</b> платёжные данные пользователей
        Платформы и не хранит данные банковских карт.
      </p>

      <h2>7. Цели обработки персональных данных</h2>
      <p>7.1. Оператор обрабатывает персональные данные в следующих целях:</p>
      <ul>
        <li>предоставление доступа к функционалу Платформы и приложений;</li>
        <li>обеспечение авторизации и безопасности учётной записи;</li>
        <li>
          отправка уведомлений, связанных с работой Платформы (подтверждение
          регистрации, восстановление пароля);
        </li>
        <li>
          отображение игровой активности, рейтинга и публикуемого контента;
        </li>
        <li>
          модерация публикуемого контента (игры, комментарии, баг-репорты) на
          предмет соответствия законодательству и правилам Платформы;
        </li>
        <li>техническая поддержка Пользователей;</li>
        <li>защита прав и интересов Оператора, Пользователей и третьих лиц.</li>
      </ul>

      <h2>8. Способы обработки персональных данных</h2>
      <p>
        8.1. Обработка персональных данных осуществляется Оператором с
        использованием средств автоматизации и без использования таковых.
      </p>
      <p>
        8.2. Персональные данные хранятся на серверах, расположенных на
        территории Российской Федерации, что соответствует требованиям ст. 18
        ч. 5 152-ФЗ.
      </p>

      <h2>9. Передача персональных данных третьим лицам</h2>
      <p>
        9.1. Оператор не передаёт персональные данные третьим лицам, за
        исключением случаев, когда это необходимо для работы сервиса или
        предусмотрено законодательством Российской Федерации.
      </p>
      <p>
        9.2. Оператор не передаёт персональные данные для рекламных или
        маркетинговых целей третьим лицам.
      </p>
      <p>9.3. Трансграничная передача персональных данных не осуществляется.</p>

      <h2>10. Сроки хранения персональных данных</h2>
      <p>
        10.1. Персональные данные хранятся в течение срока действия учётной
        записи Пользователя на Платформе.
      </p>
      <p>
        10.2. После удаления учётной записи или отзыва согласия на обработку
        Оператор прекращает обработку и уничтожает персональные данные в течение
        30 (тридцати) календарных дней, за исключением случаев, когда данные
        необходимо сохранить для защиты прав и интересов Оператора в случае
        возникшего спора (до окончания срока исковой давности).
      </p>

      <h2>11. Меры защиты персональных данных</h2>
      <p>
        11.1. Оператор принимает необходимые и достаточные правовые,
        организационные и технические меры для защиты персональных данных от
        неправомерного или случайного доступа, уничтожения, изменения,
        блокирования, копирования, распространения, а также от иных
        неправомерных действий.
      </p>
      <p>
        11.2. <b>Применяемые меры включают:</b>
      </p>
      <ul>
        <li>разграничение прав доступа к персональным данным;</li>
        <li>шифрование паролей Пользователей (односторонние хеш-функции);</li>
        <li>
          использование защищённого соединения HTTPS (TLS) при передаче данных
          между Пользователем и Платформой;
        </li>
        <li>регулярное резервное копирование данных;</li>
        <li>контроль доступа к серверному оборудованию.</li>
      </ul>

      <h2>12. Права субъектов персональных данных</h2>
      <p>12.1. Субъект персональных данных имеет право:</p>
      <ul>
        <li>
          получать от Оператора информацию, касающуюся обработки своих
          персональных данных;
        </li>
        <li>
          требовать уточнения, блокирования или уничтожения своих персональных
          данных в случае, если они являются неполными, устаревшими,
          неточными или были получены незаконно;
        </li>
        <li>отозвать согласие на обработку персональных данных;</li>
        <li>
          обжаловать действия или бездействие Оператора в Роскомнадзор или в
          судебном порядке.
        </li>
      </ul>
      <p>
        12.2. Для реализации своих прав субъект персональных данных (или его
        законный представитель) направляет письменное заявление на адрес{" "}
        <b>MaksimIvankov26@yandex.ru</b>. Оператор рассматривает заявление в
        срок, не превышающий 30 (тридцати) календарных дней.
      </p>

      <h2>13. Использование технологий хранения данных в браузере</h2>
      <p>
        13.1. Для обеспечения функциональности Платформы Оператор использует
        технологии хранения данных в браузере Пользователя (localStorage,
        sessionStorage, cookie). Такие данные включают: токен авторизации,
        идентификатор сессии, настройки интерфейса.
      </p>
      <p>
        13.2. Указанные данные хранятся исключительно на устройстве Пользователя,
        необходимы для работы сервиса и не передаются третьим лицам. При выходе
        из учётной записи авторизационные токены автоматически удаляются.
      </p>
      <p>
        13.3. Оператор не использует файлы cookie третьих лиц (рекламные сети,
        внешние системы аналитики) для отслеживания поведения Пользователей за
        пределами Платформы.
      </p>

      <h2>14. Контактная информация и обращения</h2>
      <p>
        14.1. По всем вопросам, связанным с обработкой персональных данных,
        Пользователи могут обращаться к Оператору по адресу электронной почты{" "}
        <b>MaksimIvankov26@yandex.ru</b>.
      </p>
      <p>
        14.2. Орган, уполномоченный по защите прав субъектов персональных
        данных в Российской Федерации: Федеральная служба по надзору в сфере
        связи, информационных технологий и массовых коммуникаций (
        <b>Роскомнадзор</b>), сайт:{" "}
        <a href="https://rkn.gov.ru/">https://rkn.gov.ru/</a>.
      </p>

      <h2>15. Изменение Политики</h2>
      <p>
        15.1. Оператор имеет право вносить изменения в настоящую Политику. При
        внесении изменений в заголовке Политики указывается дата последней
        редакции.
      </p>
      <p>
        15.2. Новая редакция Политики вступает в силу с момента её размещения
        на Платформе, если иное не предусмотрено новой редакцией.
      </p>
      <p>
        15.3. Действующая редакция всегда доступна по адресу{" "}
        <a href="https://rublox.pro/privacy-policy">
          https://rublox.pro/privacy-policy
        </a>
        .
      </p>
    </LegalPage>
  );
}
